import moment from 'moment-timezone';
import { asTimeZone } from '../utils/dateFormatter';
import { create, updateField, updateFields, update, remove, getById, getAll, getBySingleQuery, getByIdQuery } from '../utils/dbUtils';
import { isBooleanFalse, isBooleanTrue, isListEmpty, isListNotEmpty, isNotNullOrUndefined, isNullOrUndefined, isObjectNotEmpty, isStringEmpty, isStringNotEmpty, isStringNotEmptyOrZeroLength } from '../utils/objectUtils';
import Address from './Address';
import Customer from './Customer';
import StatusHistory from './StatusHistory';
import { collection, endBefore, getCountFromServer, getDocs, limit, limitToLast, orderBy, query, startAfter, where } from 'firebase/firestore';
import { db } from '../firebase';

class OrderGroup {
    static collectionName = 'orderGroups';

    fulfillAt = null; // date the order is placed, can be a future date for renewal orders created in advance
    refId = null; // external order/fill reference id (this IS the fillId for pharmacy submission)

    status = "CREATED"; // enum OrderGroupStatuses
    statusHistory = []; // list of StatusHistory, historical changes to status

    userRefId = null;
    userId = null; // userId of the customer

    customer = new Customer(null).toDto(); // customer details snapshot
    shippingAddress = new Address(null).toDto();

    customerNote = null; // note provided by the customer at the time they placed the order

    isFulfillable = false; // all orders in the group are ready to be fulfilled and paid for by the customer

    orderIds = []; // array of order IDs in this group

    // add ons are for things like boxes/packaging, inserts, etc that are added to the order but not part of the products being sold and cant be ordered/purchased on their own
    addOns = []; // array of OrderGroupAddOn objects
    addOnRefIds = []; // flat array of all addOns[].refId values for Firestore querying (can't query array of objects)

    shipments = []; // array of OrderShipment
    notes = []; // array of OrderNote

    carrier = null; // shipping carrier name
    trackingNumber = null; // shipment tracking number
    tracking = null;

    subscriptionIds = []; // all subscription IDs contributing orders to this group
    programIds = []; // program IDs across all subscriptions in this group

    groupType = 'STANDARD'; // enum OrderGroupTypes: STANDARD, GROUPED

    sentEmails = [];

    repId = 'NONE'; // userId of customer representative

    supplierId = null; // supplier/fulfillment partner/pharmacy to use to fulfill the order

    submittedAt = null; // date the order group is submitted to the supplier to be processed
    processingAt = null; // date the order group goes into processing status
    filledAt = null; // date the order group is done being filled
    shippedAt = null; // date the order group is shipped
    deliveredAt = null; // date the order group is delivered
    completedAt = null; // date the order group is delivered and paid for
    cancelledAt = null; // date the order group is cancelled

    isSubmitted = false;
    isProcessing = false;
    isFilled = false;
    isShipped = false;
    isDelivered = false;
    isCompleted = false;
    isCancelled = false;

    testMode = false;

    constructor(group) {
        if (isObjectNotEmpty(group)) {
            if (isNotNullOrUndefined(group.fulfillAt)) { this.fulfillAt = group.fulfillAt; }
            if (isStringNotEmpty(group.refId)) { this.refId = group.refId; }

            if (isStringNotEmpty(group.status)) { this.status = group.status; }
            if (isListNotEmpty(group.statusHistory)) { this.statusHistory = group.statusHistory; }
            else if (isStringNotEmpty(this.status)) { this.statusHistory = StatusHistory.addNewStatus(this.status, [], isNotNullOrUndefined(group.fulfillAt) ? group.fulfillAt : null); }

            if (isStringNotEmpty(group.userRefId)) { this.userRefId = group.userRefId; }
            if (isStringNotEmpty(group.userId)) { this.userId = group.userId; }

            if (isObjectNotEmpty(group.customer)) { this.customer = group.customer; }
            if (isObjectNotEmpty(group.shippingAddress)) { this.shippingAddress = group.shippingAddress; }

            if (isStringNotEmpty(group.customerNote)) { this.customerNote = group.customerNote; }

            if (isBooleanTrue(group.isFulfillable)) { this.isFulfillable = true; }

            if (isListNotEmpty(group.orderIds)) { this.orderIds = group.orderIds; }

            if (isListNotEmpty(group.addOns)) { this.addOns = group.addOns; }
            if (isListNotEmpty(group.addOnRefIds)) { this.addOnRefIds = group.addOnRefIds; }

            if (isListNotEmpty(group.shipments)) { this.shipments = group.shipments; }
            if (isListNotEmpty(group.notes)) { this.notes = group.notes; }

            if (isStringNotEmpty(group.carrier)) { this.carrier = group.carrier; }
            if (isStringNotEmpty(group.trackingNumber)) { this.trackingNumber = group.trackingNumber; }
            if (isObjectNotEmpty(group.tracking)) { this.tracking = group.tracking; }

            if (isListNotEmpty(group.subscriptionIds)) { this.subscriptionIds = group.subscriptionIds; }
            if (isListNotEmpty(group.programIds)) { this.programIds = group.programIds; }

            if (isStringNotEmpty(group.groupType)) { this.groupType = group.groupType; }

            if (isListNotEmpty(group.sentEmails)) { this.sentEmails = group.sentEmails; }

            if (isStringNotEmpty(group.repId)) { this.repId = group.repId; }

            if (isStringNotEmpty(group.supplierId)) { this.supplierId = group.supplierId; }

            if (isNotNullOrUndefined(group.submittedAt)) { this.submittedAt = group.submittedAt; }
            if (isNotNullOrUndefined(group.processingAt)) { this.processingAt = group.processingAt; }
            if (isNotNullOrUndefined(group.filledAt)) { this.filledAt = group.filledAt; }
            if (isNotNullOrUndefined(group.shippedAt)) { this.shippedAt = group.shippedAt; }
            if (isNotNullOrUndefined(group.deliveredAt)) { this.deliveredAt = group.deliveredAt; }
            if (isNotNullOrUndefined(group.completedAt)) { this.completedAt = group.completedAt; }
            if (isNotNullOrUndefined(group.cancelledAt)) { this.cancelledAt = group.cancelledAt; }

            if (isBooleanTrue(group.isSubmitted)) { this.isSubmitted = true; }
            if (isBooleanTrue(group.isProcessing)) { this.isProcessing = true; }
            if (isBooleanTrue(group.isFilled)) { this.isFilled = true; }
            if (isBooleanTrue(group.isShipped)) { this.isShipped = true; }
            if (isBooleanTrue(group.isDelivered)) { this.isDelivered = true; }
            if (isBooleanTrue(group.isCompleted)) { this.isCompleted = true; }
            if (isBooleanTrue(group.isCancelled)) { this.isCancelled = true; }

            if (isBooleanTrue(group.testMode)) { this.testMode = true; }
        }
    }

    toDto = () => {
        return {
            fulfillAt: this.fulfillAt,
            refId: this.refId,

            status: this.status,
            statusHistory: this.statusHistory,

            userRefId: this.userRefId,
            userId: this.userId,

            customer: this.customer,
            shippingAddress: this.shippingAddress,

            customerNote: this.customerNote,

            isFulfillable: this.isFulfillable,

            orderIds: this.orderIds,

            addOns: this.addOns,
            addOnRefIds: this.addOnRefIds,

            shipments: this.shipments,
            notes: this.notes,

            carrier: this.carrier,
            trackingNumber: this.trackingNumber,
            tracking: this.tracking,

            subscriptionIds: this.subscriptionIds,
            programIds: this.programIds,

            groupType: this.groupType,

            sentEmails: this.sentEmails,

            repId: this.repId,
            supplierId: this.supplierId,

            submittedAt: this.submittedAt,
            processingAt: this.processingAt,
            filledAt: this.filledAt,
            shippedAt: this.shippedAt,
            deliveredAt: this.deliveredAt,
            completedAt: this.completedAt,
            cancelledAt: this.cancelledAt,

            isSubmitted: this.isSubmitted,
            isProcessing: this.isProcessing,
            isFilled: this.isFilled,
            isShipped: this.isShipped,
            isDelivered: this.isDelivered,
            isCompleted: this.isCompleted,
            isCancelled: this.isCancelled,

            testMode: this.testMode
        };
    }

    isValid = () => {
        if (isStringEmpty(this.userId)) {
            throw new Error('userId is required.');
        }

        if (isListEmpty(this.orderIds)) {
            throw new Error('orderIds must contain at least one order id.');
        }

        return true;
    }

    create = async () => {
        if (this.isValid()) {
            return await create(OrderGroup.collectionName, this.toDto());
        }
    }

    static updateField = async (id, fieldName, newValue) => {
        if (isStringNotEmpty(id)) {
            return await updateField(OrderGroup.collectionName, id, fieldName, newValue);
        }
        throw new Error('id is required.');
    }

    static updateFields = async (id, updatedFields) => {
        if (isStringNotEmpty(id)) {
            return await updateFields(OrderGroup.collectionName, id, updatedFields);
        }
        throw new Error('id is required.');
    }

    static update = async (id, updatedGroup) => {
        const updatedObj = new OrderGroup(updatedGroup);
        if (updatedObj.isValid() && id === updatedGroup.id) {
            return await update(OrderGroup.collectionName, id, updatedObj.toDto());
        }
        throw new Error('id and id field of the updated order group must match.');
    }

    static delete = async (id) => {
        if (isStringNotEmpty(id)) {
            return await remove(OrderGroup.collectionName, id);
        }
        throw new Error('id is required.');
    }

    static getById = async (id) => {
        if (isStringNotEmpty(id)) {
            return await getById(OrderGroup.collectionName, id);
        }
        throw new Error('id is required.');
    }

    static getByRefId = async (refId) => {
        if (isStringNotEmpty(refId)) {
            return await getBySingleQuery(OrderGroup.collectionName, 'refId', refId, '==');
        }
        throw new Error('refId is required.');
    }

    static getAll = async () => {
        return await getAll(OrderGroup.collectionName);
    }

    static getByField = async (fieldName, fieldValue, comparer = '==') => {
        return await getBySingleQuery(OrderGroup.collectionName, fieldName, fieldValue, comparer);
    }

    static getByUserId = async (userId) => {
        return await getBySingleQuery(OrderGroup.collectionName, 'userId', userId, '==');
    };

    static getBySubscriptionId = async (subscriptionId) => {
        return await getBySingleQuery(OrderGroup.collectionName, 'subscriptionIds', subscriptionId, 'array-contains');
    };

    static getByOrderId = async (orderId) => {
        if (isStringNotEmpty(orderId)) {
            return await getBySingleQuery(OrderGroup.collectionName, 'orderIds', orderId, 'array-contains');
        }
        throw new Error('orderId is required.');
    };

    static getByOrderIds = async (orderIds = []) => {
        if (!isListNotEmpty(orderIds)) {
            return [];
        }

        const uniqueOrderIds = [...new Set(orderIds.filter(isStringNotEmpty))];
        if (!isListNotEmpty(uniqueOrderIds)) {
            return [];
        }

        return await getBySingleQuery(OrderGroup.collectionName, 'orderIds', uniqueOrderIds, 'array-contains-any');
    };

    // patients can only query their own docs, so scope by userId and match orderIds client-side;
    // an orderIds-only query fails the isUser(resource.data.userId) rule for non-staff
    static getByUserIdAndOrderIds = async (userId, orderIds = []) => {
        if (isStringEmpty(userId)) {
            return await OrderGroup.getByOrderIds(orderIds);
        }

        const uniqueOrderIds = new Set((orderIds || []).filter(isStringNotEmpty));
        if (isListEmpty([...uniqueOrderIds])) {
            return [];
        }

        const userOrderGroups = await OrderGroup.getByUserId(userId);
        return isListNotEmpty(userOrderGroups)
            ? userOrderGroups.filter((orderGroup) => isListNotEmpty(orderGroup?.orderIds) && orderGroup.orderIds.some((orderId) => uniqueOrderIds.has(orderId)))
            : [];
    };

    static getByAddOnRefId = async (addOnRefId) => {
        if (isStringNotEmpty(addOnRefId)) {
            return await getBySingleQuery(OrderGroup.collectionName, 'addOnRefIds', addOnRefId, 'array-contains');
        }
        throw new Error('addOnRefId is required.');
    };

    static getByIds = async (ids = []) => {
        return await getByIdQuery(OrderGroup.collectionName, ids, 'in');
    };

    static getByQueryConstraints = async (queryConstraints = []) => {
        const q = query(collection(db, OrderGroup.collectionName), ...queryConstraints);
        const snapShot = await getDocs(q);

        return snapShot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data()
        }));
    };

    static search = async (searchParams = {}) => {
        const {
            userId,
            subscriptionId,
            status,
            isFulfillable,
            groupType,
            repId,
            supplierId,
            testMode,
            fulfillStart,
            fulfillEnd,
            orderId,
            refId
        } = searchParams;

        const queryConstraints = [];

        if (isListNotEmpty(userId)) {
            queryConstraints.push(where('userId', 'in', userId));
        } else if (isStringNotEmptyOrZeroLength(userId)) {
            queryConstraints.push(where('userId', '==', userId));
        }

        if (isStringNotEmptyOrZeroLength(subscriptionId)) {
            queryConstraints.push(where('subscriptionIds', 'array-contains', subscriptionId));
        } else if (isStringNotEmptyOrZeroLength(orderId)) {
            queryConstraints.push(where('orderIds', 'array-contains', orderId));
        }

        if (isListNotEmpty(status)) {
            queryConstraints.push(where('status', 'in', status));
        } else if (isStringNotEmptyOrZeroLength(status)) {
            queryConstraints.push(where('status', '==', status));
        }

        if (isBooleanTrue(isFulfillable)) {
            queryConstraints.push(where('isFulfillable', '==', true));
        } else if (isBooleanFalse(isFulfillable)) {
            queryConstraints.push(where('isFulfillable', '==', false));
        }

        if (isStringNotEmptyOrZeroLength(groupType)) {
            queryConstraints.push(where('groupType', '==', groupType));
        }

        if (isListNotEmpty(repId)) {
            queryConstraints.push(where('repId', 'in', repId));
        } else if (isStringNotEmptyOrZeroLength(repId)) {
            queryConstraints.push(where('repId', '==', repId));
        }

        if (isListNotEmpty(supplierId)) {
            queryConstraints.push(where('supplierId', 'in', supplierId));
        } else if (isStringNotEmptyOrZeroLength(supplierId)) {
            queryConstraints.push(where('supplierId', '==', supplierId));
        }

        if (isBooleanTrue(testMode)) {
            queryConstraints.push(where('testMode', '==', true));
        } else if (isBooleanFalse(testMode)) {
            queryConstraints.push(where('testMode', '==', false));
        }

        if (isNotNullOrUndefined(fulfillStart)) {
            const startDate = asTimeZone(moment(new Date(fulfillStart)), 'US/Pacific').startOf('day').utc().toDate();
            queryConstraints.push(where('fulfillAt', '>=', startDate));
        }

        if (isNotNullOrUndefined(fulfillEnd)) {
            const endDate = asTimeZone(moment(new Date(fulfillEnd)), 'US/Pacific').endOf('day').utc().toDate();
            queryConstraints.push(where('fulfillAt', '<=', endDate));
        }

        if (isStringNotEmptyOrZeroLength(refId)) {
            queryConstraints.push(where('refId', '==', refId));
        }

        queryConstraints.push(orderBy('fulfillAt', 'asc'));
        queryConstraints.push(orderBy('__name__', 'asc'));

        return await this.getByQueryConstraints(queryConstraints);
    };

    static getCount = async (queryParameters = {}) => {
        const {
            testMode,
            userId,
            status,
            isFulfillable,
            groupType,
            repId,
            supplierId,
            subscriptionId,
            fulfillStart,
            fulfillEnd
        } = queryParameters;

        const queryConstraints = [];

        if (isBooleanTrue(testMode)) {
            queryConstraints.push(where('testMode', '==', true));
        } else if (isBooleanFalse(testMode)) {
            queryConstraints.push(where('testMode', '==', false));
        }

        if (isListNotEmpty(userId)) {
            queryConstraints.push(where('userId', 'in', userId));
        } else if (isStringNotEmptyOrZeroLength(userId)) {
            queryConstraints.push(where('userId', '==', userId));
        }

        if (isStringNotEmptyOrZeroLength(subscriptionId)) {
            queryConstraints.push(where('subscriptionIds', 'array-contains', subscriptionId));
        }

        if (isListNotEmpty(status)) {
            queryConstraints.push(where('status', 'in', status));
        } else if (isStringNotEmptyOrZeroLength(status)) {
            queryConstraints.push(where('status', '==', status));
        }

        if (isBooleanTrue(isFulfillable)) {
            queryConstraints.push(where('isFulfillable', '==', true));
        } else if (isBooleanFalse(isFulfillable)) {
            queryConstraints.push(where('isFulfillable', '==', false));
        }

        if (isStringNotEmptyOrZeroLength(groupType)) {
            queryConstraints.push(where('groupType', '==', groupType));
        }

        if (isListNotEmpty(repId)) {
            queryConstraints.push(where('repId', 'in', repId));
        } else if (isStringNotEmptyOrZeroLength(repId)) {
            queryConstraints.push(where('repId', '==', repId));
        }

        if (isListNotEmpty(supplierId)) {
            queryConstraints.push(where('supplierId', 'in', supplierId));
        } else if (isStringNotEmptyOrZeroLength(supplierId)) {
            queryConstraints.push(where('supplierId', '==', supplierId));
        }

        if (isNotNullOrUndefined(fulfillStart)) {
            const startDate = asTimeZone(moment(new Date(fulfillStart)), 'US/Pacific').startOf('day').utc().toDate();
            queryConstraints.push(where('fulfillAt', '>=', startDate));
        }

        if (isNotNullOrUndefined(fulfillEnd)) {
            const endDate = asTimeZone(moment(new Date(fulfillEnd)), 'US/Pacific').endOf('day').utc().toDate();
            queryConstraints.push(where('fulfillAt', '<=', endDate));
        }

        const q = query(collection(db, OrderGroup.collectionName), ...queryConstraints);
        const snapShot = await getCountFromServer(q);

        return snapShot.data().count;
    };

    static getPaginated = async (queryParameters = {}, pageSize = 25, firstVisible, lastVisible, orderByField = 'fulfillAt') => {
        const {
            testMode,
            userId,
            status,
            isFulfillable,
            groupType,
            repId,
            supplierId,
            subscriptionId,
            fulfillStart,
            fulfillEnd
        } = queryParameters;

        const queryConstraints = [];

        if (isBooleanTrue(testMode)) {
            queryConstraints.push(where('testMode', '==', true));
        } else if (isBooleanFalse(testMode)) {
            queryConstraints.push(where('testMode', '==', false));
        }

        if (isListNotEmpty(userId)) {
            queryConstraints.push(where('userId', 'in', userId));
        } else if (isStringNotEmptyOrZeroLength(userId)) {
            queryConstraints.push(where('userId', '==', userId));
        }

        if (isStringNotEmptyOrZeroLength(subscriptionId)) {
            queryConstraints.push(where('subscriptionIds', 'array-contains', subscriptionId));
        }

        if (isListNotEmpty(status)) {
            queryConstraints.push(where('status', 'in', status));
        } else if (isStringNotEmptyOrZeroLength(status)) {
            queryConstraints.push(where('status', '==', status));
        }

        if (isBooleanTrue(isFulfillable)) {
            queryConstraints.push(where('isFulfillable', '==', true));
        } else if (isBooleanFalse(isFulfillable)) {
            queryConstraints.push(where('isFulfillable', '==', false));
        }

        if (isStringNotEmptyOrZeroLength(groupType)) {
            queryConstraints.push(where('groupType', '==', groupType));
        }

        if (isListNotEmpty(repId)) {
            queryConstraints.push(where('repId', 'in', repId));
        } else if (isStringNotEmptyOrZeroLength(repId)) {
            queryConstraints.push(where('repId', '==', repId));
        }

        if (isListNotEmpty(supplierId)) {
            queryConstraints.push(where('supplierId', 'in', supplierId));
        } else if (isStringNotEmptyOrZeroLength(supplierId)) {
            queryConstraints.push(where('supplierId', '==', supplierId));
        }

        if (isNotNullOrUndefined(fulfillStart)) {
            const startDate = asTimeZone(moment(new Date(fulfillStart)), 'US/Pacific').startOf('day').utc().toDate();
            queryConstraints.push(where('fulfillAt', '>=', startDate));
        }

        if (isNotNullOrUndefined(fulfillEnd)) {
            const endDate = asTimeZone(moment(new Date(fulfillEnd)), 'US/Pacific').endOf('day').utc().toDate();
            queryConstraints.push(where('fulfillAt', '<=', endDate));
        }

        let q = null;

        if (isObjectNotEmpty(firstVisible)) {
            q = query(
                collection(db, OrderGroup.collectionName),
                ...queryConstraints,
                orderBy(orderByField, 'asc'),
                orderBy('__name__', 'asc'),
                limitToLast(pageSize),
                endBefore(firstVisible[orderByField], firstVisible.id)
            );
        } else if (isObjectNotEmpty(lastVisible)) {
            q = query(
                collection(db, OrderGroup.collectionName),
                ...queryConstraints,
                orderBy(orderByField, 'asc'),
                orderBy('__name__', 'asc'),
                limit(pageSize),
                startAfter(lastVisible[orderByField], lastVisible.id)
            );
        } else {
            q = query(
                collection(db, OrderGroup.collectionName),
                ...queryConstraints,
                orderBy(orderByField, 'asc'),
                orderBy('__name__', 'asc'),
                limit(pageSize)
            );
        }

        const snapshot = await getDocs(q);
        return snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data()
        }));
    };

    static get = async (queryParameters = {}) => {
        const allRecords = [];
        try {
            let next = null;

            const totalCount = await OrderGroup.getCount(queryParameters);
            const pageSize = 1000;
            const totalPages = Math.ceil(totalCount / pageSize);
            const pages = Array.from({ length: totalPages }, (_, idx) => idx + 1);

            for (const page of pages) {
                const list = await OrderGroup.getPaginated(queryParameters, pageSize, null, next, 'fulfillAt');

                allRecords.push(...list);

                next = list[list.length - 1];
                if (!next) {
                    break;
                }
            }
        } catch (error) {
            console.log(error);
        }

        return allRecords;
    };
}

export default OrderGroup;
